/**
 * TalkRecoveryOffer — the Keep / Let it disappear offer for a Talk that was cut off.
 *
 * WHY THIS IS ITS OWN COMPONENT
 * The offer used to live inside RealtimeVoiceMode, which only mounts when a member starts a
 * brand-new live voice session. Real-device evidence showed the member arriving at Talk and at
 * Saved Talks looking for the lost conversation without ever mounting the live voice engine —
 * so the pending request was never even sent and the offer was unreachable. Recovery is a
 * property of the Talk surface, not of a WebRTC connection, so it is mounted here independently
 * of any session, permission prompt or connection failure.
 *
 * PRIVACY: nothing here makes anything durable. Only Keep does. Dismissing leaves the
 * conversation exactly where it was — inside its existing retention window.
 */

import React, { useEffect, useRef, useState } from 'react';
import VoicePrivacyModal from './VoicePrivacyModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Two Talk surfaces can be mounted at once (Saved Talks opens over Talk). Only one presents
// the offer, so the member is never asked about the same conversation twice.
let offerClaimed = false;

// Persisted device trace. Safari on a phone gives no console, so every step of the recovery
// path is recorded server-side (metadata only, 30-day TTL) — this is how a real-device failure
// is diagnosed instead of guessed.
const trace = (stage, metadata = {}) => {
  try {
    fetch(`${API_URL}/api/auth/diagnostic`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type: 'talk_recovery',
        stage,
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
        metadata
      })
    });
  } catch (err) {
    // Tracing must never affect recovery.
  }
};

const fetchWithAuthRetry = async (url, options = {}) => {
  let response = await fetch(url, options);
  let refreshed = false;
  if (response.status === 401) {
    const r = await fetch(`${API_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
    refreshed = r.ok;
    if (r.ok) response = await fetch(url, options);
  }
  return { response, refreshed };
};

const TalkRecoveryOffer = ({ surface = 'talk', onKept, onResolved }) => {
  const [offer, setOffer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mountedRef = useRef(true);
  const claimedRef = useRef(false);
  const decidedRef = useRef(false);

  const checkForOffer = async () => {
    trace('check_sent', { surface });
    try {
      const { response, refreshed } = await fetchWithAuthRetry(
        `${API_URL}/api/realtime/transcript/pending`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        trace('check_failed', { surface, status: response.status, refreshed });
        return;
      }
      const data = await response.json();
      if (!data.pending || !data.message_count) {
        trace('nothing_to_offer', { surface, refreshed, pending: !!data.pending });
        return;
      }
      if (!mountedRef.current) {
        trace('discarded_unmounted', { surface, transcript_id: data.id });
        return;
      }
      if (offerClaimed && !claimedRef.current) {
        trace('offer_claimed_elsewhere', { surface });
        return;
      }
      offerClaimed = true;
      claimedRef.current = true;
      setOffer({ ...data, interrupted: true });
      trace('offer_shown', {
        surface,
        refreshed,
        transcript_id: data.id,
        message_count: data.message_count
      });
    } catch (err) {
      trace('check_error', { surface, error: String(err).slice(0, 200) });
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    checkForOffer();
    return () => {
      mountedRef.current = false;
      if (claimedRef.current) {
        offerClaimed = false;
        claimedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (decision) => {
    if (!offer) return;
    setIsSubmitting(true);
    try {
      const { response } = await fetchWithAuthRetry(
        `${API_URL}/api/realtime/transcript/${offer.id}/decision`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ decision })
        }
      );
      if (response.ok) {
        decidedRef.current = true;
        trace('decision_recorded', { surface, decision, transcript_id: offer.id });
        if (decision === 'save') onKept?.();
        // F-410-B. The surface needs to know the decision is settled, so Talk can become
        // reachable again immediately rather than after the member exits and taps once more.
        onResolved?.(decision);
      } else {
        trace('decision_failed', { surface, decision, status: response.status });
      }
    } catch (err) {
      trace('decision_error', { surface, decision, error: String(err).slice(0, 200) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    const decided = decidedRef.current;
    decidedRef.current = false;
    setOffer(null);
    // More than one Talk can have been left undecided. Once this one is decided, offer the next
    // in the same sitting. Dismissing without deciding must never re-ask the same conversation.
    if (decided) {
      setTimeout(() => {
        if (mountedRef.current) checkForOffer();
      }, 400);
    } else {
      trace('offer_dismissed', { surface });
      onResolved?.('dismissed');
      offerClaimed = false;
      claimedRef.current = false;
    }
  };

  if (!offer) return null;

  return (
    <div data-testid="talk-recovery-offer">
      <VoicePrivacyModal
        isOpen={!!offer}
        onClose={handleClose}
        onSave={() => decide('save')}
        onLetExpire={() => decide('expire')}
        transcriptInfo={offer}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default TalkRecoveryOffer;
